import type { ComponentType } from "react";

import { HelpMascot } from "./HelpMascot";
import { PlaceholderSilhouette } from "./PlaceholderSilhouette";

export type MascotName = "help" | "empty-projects" | "onboarding";

export type MascotSpec = {
  /**
   * Rendered art; takes the `mascot-art` class and fills the slot.
   */
  art: ComponentType<{ className?: string }>;
  placeholder: boolean;
  width: number;
  height: number;
  anchor: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  /**
   * Offset of the art's box from the anchor corner, in px.
   */
  offset: [number, number];
  rotate: number;
};

/**
 * Injection point for the mascot art. `help` is Heidi sitting and thinking,
 * upright, her box at the help panel's top-left corner. Every box here
 * keeps her drawing's 374×357 proportions, because
 * `mascot-art` contains rather than stretches and a box of another shape
 * only letterboxes her inside it. The others are placeholders until their
 * art lands; set `placeholder: false` when it does.
 */
export const mascots: Record<MascotName, MascotSpec> = {
  // As tall as the slot allows: the panel above her carries `z-index: 1`, so
  // anything of her that reaches past the gutter between the two panels is
  // painted over, and her ears come out cut flat.
  help: {
    art: HelpMascot,
    placeholder: false,
    width: 125,
    height: 119,
    anchor: "top-left",
    offset: [-32, -24],
    rotate: 0,
  },
  // Leaning on the panel's bottom-left corner, beside the button rather
  // than over the words: the empty state centres its text now, and at the
  // top-left the raccoon sat on the first line of it.
  "empty-projects": {
    art: HelpMascot,
    placeholder: false,
    width: 110,
    height: 105,
    anchor: "bottom-left",
    offset: [-34, -12],
    rotate: 0,
  },
  onboarding: {
    art: PlaceholderSilhouette,
    placeholder: true,
    width: 120,
    height: 130,
    anchor: "top-right",
    offset: [16, -40],
    rotate: 6,
  },
};

/**
 * Where the peeking drawing meets the thing she peeks round, in CSS px of its
 * box at full size: `ledge` is the vertical line her paws grip, which goes on
 * the target's left edge, and `bodyTop`/`bodyBottom` are the outsides of her
 * two arms, the height of body the target has to hide.
 */
export const peekArt = {
  width: 126.3,
  height: 115.3,
  ledge: 77.1,
  bodyTop: 30,
  bodyBottom: 110.2,
} as const;
